import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../services/useAuth";
import { hasPageAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { withAuth } from "../../../components/HOC/withAuth";
import {
  formatDateTimeTrInAppTz,
  formatInstantDateShortTr,
} from "../../../utils/appTime";
import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import Button from "../../../components/BaseUI/Button";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Panel,
} from "../../../components/BaseUI/Tabs";
import {
  FiArrowLeft,
  FiMail,
  FiPhone,
  FiMapPin,
  FiUsers,
  FiShoppingCart,
  FiPackage,
  FiTruck,
  FiUser,
  FiCalendar,
  FiHash,
  FiChevronDown,
  FiChevronRight,
  FiCopy,
  FiExternalLink,
  FiEye,
  FiHome,
  FiList,
  FiSearch,
  FiLock,
} from "react-icons/fi";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import IconButton from "../../../components/BaseUI/Button/IconButton";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const CompanyDetails = () => {
  const { code: companyId } = useParams();
  const { getToken, user } = useAuth();
  const navigate = useNavigate();
  const userPages = user?.pages || [];
  const userRole = user?.role || "";
  const hasSalesPage = hasPageAccess(
    userRole,
    [],
    "/device-sales",
    userPages,
    !!user?.fullAccess,
  );
  const hasDeliveriesPage = hasPageAccess(
    userRole,
    [],
    "/deliveries",
    userPages,
    !!user?.fullAccess,
  );
  const format = useFormatValue;

  const [company, setCompany] = useState(null);
  const [sales, setSales] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [expandedSales, setExpandedSales] = useState({});
  const [serialModal, setSerialModal] = useState(null);
  const [saleModelsModal, setSaleModelsModal] = useState(null);
  const [deliveryModelsModalOpen, setDeliveryModelsModalOpen] = useState(false);
  const [selectedDeliveryForModels, setSelectedDeliveryForModels] =
    useState(null);
  const [salesDenied, setSalesDenied] = useState(false);
  const [deliveriesDenied, setDeliveriesDenied] = useState(false);

  useTitle("Rovion MES - Firma Detayları");

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: translate("UI.COMPANIES"), url: "/companies" },
    { label: company?.name || companyId },
  ];

  useEffect(() => {
    fetchAll();
  }, [companyId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchCompany(), fetchSales(), fetchDeliveries()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompany = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.data.success) setCompany(res.data.company);
    } catch (err) {
      addToast(
        err.response?.data?.message || "Firma bilgileri yüklenemedi",
        "danger",
      );
    }
  };

  const fetchSales = async () => {
    setSalesDenied(false);
    try {
      const res = await axios.get(`${API_URL}/api/device-sales`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { companyId: companyId },
      });
      const all = Array.isArray(res.data) ? res.data : res.data?.sales || [];
      const filtered = all.filter(
        (s) =>
          s.companyId?._id === companyId ||
          s.companyId === companyId ||
          s.companyCode === company?.code,
      );
      setSales(filtered);
    } catch (err) {
      if (err?.response?.status === 403) setSalesDenied(true);
      else setSales([]);
    }
  };

  const fetchDeliveries = async () => {
    setDeliveriesDenied(false);
    try {
      const res = await axios.get(`${API_URL}/api/deliveries`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const all = Array.isArray(res.data)
        ? res.data
        : res.data?.deliveries || [];
      const filtered = all.filter(
        (d) =>
          d.saleId?.companyId?._id === companyId ||
          d.toCompanyId?._id === companyId ||
          d.fromCompanyId?._id === companyId ||
          d.saleId?.companyId?.code === company?.code ||
          d.saleId?.companyCode === company?.code ||
          d.companyCode === company?.code,
      );
      setDeliveries(filtered);
    } catch (err) {
      if (err?.response?.status === 403) setDeliveriesDenied(true);
      else setDeliveries([]);
    }
  };

  const addToast = (title, appearance = "info") =>
    setToasts((prev) => [...prev, { id: Date.now(), title, appearance }]);

  const removeToast = (id) =>
    setToasts((prev) => prev.filter((t) => t.id !== id));

  const toggleSaleExpand = (saleId) =>
    setExpandedSales((prev) => ({ ...prev, [saleId]: !prev[saleId] }));

  const openSerialModal = (delivery) => {
    const models = groupByModel(delivery.devices);
    setSerialModal({
      id: delivery._id,
      saleName: delivery.saleId?.saleName,
      date: delivery.deliveryDate || delivery.createdAt,
      models,
      total: delivery.devices?.length || 0,
    });
  };

  const buildSaleModelsBreakdown = (sale, saleDeliveries) => {
    const map = {};

    // Ordered / should deliver from sale.models
    (sale.models || []).forEach((m) => {
      const modelObj = m.modelId || m.model || {};
      const name = modelObj.name || m.name || "Bilinmeyen Model";
      const code = modelObj.code || m.code || "";
      const key = code || name;

      if (!map[key]) {
        map[key] = { name, code, ordered: 0, delivered: 0 };
      }

      const count = m.count ?? m.quantity ?? m.qty ?? m.amount ?? m.total ?? 0;
      map[key].ordered += count;
    });

    // Delivered from all deliveries' devices
    saleDeliveries.forEach((delivery) => {
      (delivery.devices || []).forEach((dev) => {
        const name =
          dev?.modelId?.name || dev?.model?.name || "Bilinmeyen Model";
        const code = dev?.modelId?.code || dev?.model?.code || "";
        const key = code || name;

        if (!map[key]) {
          map[key] = { name, code, ordered: 0, delivered: 0 };
        }
        map[key].delivered += 1;
      });
    });

    return Object.values(map)
      .map((m) => ({
        ...m,
        pending: Math.max((m.ordered || 0) - (m.delivered || 0), 0),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const openSaleModelsModal = (sale, saleDeliveries) => {
    const models = buildSaleModelsBreakdown(sale, saleDeliveries);
    const totals = models.reduce(
      (acc, m) => {
        acc.ordered += m.ordered || 0;
        acc.delivered += m.delivered || 0;
        acc.pending += m.pending || 0;
        return acc;
      },
      { ordered: 0, delivered: 0, pending: 0 },
    );

    setSaleModelsModal({
      id: sale._id,
      saleName: sale.saleName || `Sipariş #${sale._id?.slice(-6)}`,
      createdAt: sale.createdAt,
      models,
      totals,
    });
  };

  const getStatusAppearance = (status) => {
    switch (status) {
      case "active":
      case "completed":
        return "success-subtle";
      case "inactive":
      case "cancelled":
        return "danger-subtle";
      case "pending":
      case "partial":
        return "warning-subtle";
      default:
        return "neutral-subtle";
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return formatInstantDateShortTr(d);
  };

  const formatDateTime = (d) => {
    if (!d) return "-";
    return formatDateTimeTrInAppTz(d);
  };

  const getSaleDeliveries = (saleId) =>
    deliveries.filter((d) => d.saleId?._id === saleId || d.saleId === saleId);

  const groupByModel = (devices = []) => {
    const map = {};
    devices.forEach((dev) => {
      const name = dev?.modelId?.name || dev?.model?.name || "Bilinmeyen Model";
      if (!map[name])
        map[name] = {
          name,
          code: dev?.modelId?.code || dev?.model?.code || "",
          devices: [],
        };
      map[name].devices.push(dev);
    });
    return Object.values(map);
  };

  const totalDevicesDelivered = deliveries.reduce(
    (sum, d) => sum + (d.devices?.length || 0),
    0,
  );

  const limitToThreeWords = (text) => {
    if (!text) return "-";
    return text.trim().split(/\s+/).slice(0, 3).join(" ");
  };

  const handleViewDeliveryModels = (delivery) => {
    setSelectedDeliveryForModels(delivery);
    setDeliveryModelsModalOpen(true);
  };

  /* ─── Serial Numbers Modal ─── */
  const SerialModal = () => {
    const [query, setQuery] = useState("");
    const [expandedModels, setExpandedModels] = useState({});

    if (!serialModal) return null;

    const q = query.trim().toLowerCase();
    const filteredModels = (serialModal.models || [])
      .map((m) => {
        const modelMatch =
          !q ||
          (m.name || "").toLowerCase().includes(q) ||
          (m.code || "").toLowerCase().includes(q);

        const devices = modelMatch
          ? m.devices || []
          : (m.devices || []).filter((d) =>
              (d?.serialNumber || "").toLowerCase().includes(q),
            );

        return { ...m, devices };
      })
      .filter((m) => (q ? (m.devices || []).length > 0 : true));

    const visibleTotal = filteredModels.reduce(
      (sum, m) => sum + (m.devices?.length || 0),
      0,
    );

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        addToast("Kopyalandı", "success");
      } catch {
        addToast("Kopyalanamadı", "danger");
      }
    };

    const toggleModel = (key) =>
      setExpandedModels((p) => ({ ...p, [key]: !p[key] }));

    return (
      <Modal isOpen={!!serialModal} onClose={() => setSerialModal(null)}>
        <ModalHeader>
          <div className="d-flex align-items-center gap-2">
            <FiList size={18} />
            <span>
              Seri Numaraları
              {serialModal.saleName && (
                <span className="fw-normal text-muted ml-2 small">
                  — {serialModal.saleName}
                </span>
              )}
            </span>
          </div>
        </ModalHeader>
        <ModalBody>
          {/* Summary */}
          <div
            className="rounded-3 p-3 mb-3"
            style={{
              background: "var(--bs-light, #f8f9fa)",
              border: "1px solid var(--bs-border-color, #dee2e6)",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                gap: 12,
                alignItems: "center",
              }}
            >
              <div>
                <div className="fw-bold" style={{ fontSize: "1.05rem" }}>
                  {q ? visibleTotal : serialModal.total}
                </div>
                <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                  {q ? "Gösterilen ürün" : "Toplam ürün"}
                </div>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: "1.05rem" }}>
                  {filteredModels.length}
                </div>
                <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                  Model
                </div>
              </div>
              <div className="d-flex align-items-center gap-1 text-muted">
                <FiCalendar size={13} />
                <span style={{ fontSize: "0.78rem" }}>
                  {formatDate(serialModal.date)}
                </span>
              </div>
            </div>
          </div>

          {/* Models */}
          {filteredModels.length === 0 ? (
            <div className="text-center text-muted small py-4">
              Sonuç bulunamadı.
            </div>
          ) : (
            filteredModels.map((model, mi) => {
              const key = model.code || model.name || String(mi);
              const isOpen = q ? true : (expandedModels[key] ?? mi === 0);
              const serials = (model.devices || [])
                .map((d) => d?.serialNumber)
                .filter(Boolean);
              const allSerialsText = serials.join("\n");

              return (
                <div
                  key={key}
                  className="rounded-3 mb-3"
                  style={{
                    border: "1px solid var(--bs-border-color, #dee2e6)",
                    overflow: "hidden",
                    background: "var(--bs-body-bg, #fff)",
                  }}
                >
                  <div
                    className="d-flex align-items-center justify-content-between gap-2 px-3 py-2"
                    style={{
                      background: "var(--bs-light, #f8f9fa)",
                      borderBottom: "1px solid var(--bs-border-color, #dee2e6)",
                      cursor: "pointer",
                    }}
                    onClick={() => toggleModel(key)}
                  >
                    <div className="d-flex align-items-center gap-2 min-w-0">
                      <FiPackage size={14} className="text-muted" />
                      <span className="fw-semibold small text-truncate">
                        {model.name}
                      </span>
                      {model.code && (
                        <code
                          className="text-muted"
                          style={{ fontSize: "0.72rem" }}
                        >
                          {model.code}
                        </code>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <Lozenge appearance="neutral-subtle">
                        {model.devices?.length || 0} adet
                      </Lozenge>
                      <button
                        className="btn btn-sm d-flex align-items-center gap-1"
                        style={{
                          fontSize: "0.72rem",
                          padding: "3px 10px",
                          background: "transparent",
                          color: "var(--bs-secondary-color, #6c757d)",
                          border: "1px solid var(--bs-border-color, #dee2e6)",
                          borderRadius: 6,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (allSerialsText) copyToClipboard(allSerialsText);
                        }}
                        disabled={!allSerialsText}
                        title="Bu modeldeki tüm seri numaralarını kopyala"
                      >
                        <FiCopy size={12} />
                        Kopyala
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="p-3">
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(160px, 1fr))",
                          gap: 8,
                          maxHeight: 260,
                          overflowY: "auto",
                        }}
                      >
                        {(model.devices || []).map((dev, di) => {
                          const sn = dev?.serialNumber || "-";
                          const isMissing = !dev?.serialNumber;
                          return (
                            <button
                              key={di}
                              type="button"
                              className="btn text-start"
                              style={{
                                padding: "8px 10px",
                                borderRadius: 10,
                                background: "var(--bs-body-bg, #fff)",
                                border:
                                  "1px solid var(--bs-border-color, #dee2e6)",
                                fontFamily: "monospace",
                                fontSize: "0.78rem",
                                color: isMissing
                                  ? "var(--bs-secondary-color, #6c757d)"
                                  : "var(--bs-body-color, #212529)",
                              }}
                              onClick={() => {
                                if (!isMissing) copyToClipboard(sn);
                              }}
                              disabled={isMissing}
                              title={
                                isMissing ? "Seri numarası yok" : "Kopyala"
                              }
                            >
                              {sn}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="danger-subtle"
            onClick={() => setSerialModal(null)}
          >
            Kapat
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  /* ─── Sale Models (ordered vs delivered) Modal ─── */
  const SaleModelsModal = () => {
    if (!saleModelsModal) return null;

    return (
      <Modal
        isOpen={!!saleModelsModal}
        onClose={() => setSaleModelsModal(null)}
      >
        <ModalHeader>
          <div className="d-flex align-items-center gap-2">
            <FiPackage size={18} />
            <span>
              Sipariş Ürün Durumu
              {saleModelsModal.saleName && (
                <span className="fw-normal text-muted ml-2 small">
                  — {saleModelsModal.saleName}
                </span>
              )}
            </span>
          </div>
        </ModalHeader>
        <ModalBody>
          <div
            className="d-flex align-items-center gap-3 mb-4 p-3 rounded"
            style={{ background: "var(--bs-light, #f8f9fa)" }}
          >
            <div className="text-center">
              <div className="h5 mb-0 fw-bold">
                {saleModelsModal.totals.ordered}
              </div>
              <small className="text-muted">Toplam</small>
            </div>
            <div className="vr" />
            <div className="text-center">
              <div className="h5 mb-0 fw-bold text-success">
                {saleModelsModal.totals.delivered}
              </div>
              <small className="text-muted">Teslim Edilen</small>
            </div>
            <div className="vr" />
            <div className="text-center">
              <div className="h5 mb-0 fw-bold text-warning">
                {saleModelsModal.totals.pending}
              </div>
              <small className="text-muted">Kalan</small>
            </div>
          </div>

          {saleModelsModal.models.length === 0 ? (
            <div className="text-center text-muted small">
              Bu sipariş için model bilgisi bulunamadı.
            </div>
          ) : (
            saleModelsModal.models.map((m, i) => (
              <div
                key={i}
                className="mb-3 p-2 rounded-3"
                style={{
                  border: "1px solid var(--bs-border-color, #dee2e6)",
                  background: "var(--bs-body-bg, #fff)",
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="d-flex align-items-center gap-2">
                    <FiPackage size={14} className="text-muted" />
                    <span className="fw-semibold small">{m.name}</span>
                    {m.code && (
                      <code
                        className="text-muted"
                        style={{ fontSize: "0.72rem" }}
                      >
                        {m.code}
                      </code>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2 small">
                    <span className="text-muted">
                      Toplam:{" "}
                      <span className="fw-semibold">{m.ordered || 0}</span>
                    </span>
                    <span className="text-success">
                      Teslim Edilen:{" "}
                      <span className="fw-semibold">{m.delivered || 0}</span>
                    </span>
                    <span className="text-warning">
                      Kalan:{" "}
                      <span className="fw-semibold">{m.pending || 0}</span>
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="danger-subtle"
            onClick={() => setSaleModelsModal(null)}
          >
            Kapat
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  /* ─── Loading / not found states ─── */
  if (loading) {
    return (
      <AppPage title="Firma Detayları">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 300 }}
        >
          <div className="spinner-border text-primary" />
        </div>
      </AppPage>
    );
  }

  if (!company) {
    return (
      <AppPage title="Firma Detayları">
        <div className="card card-body text-center py-5">
          <FiHome size={48} className="text-muted mb-3 mx-auto" />
          <p className="text-muted mb-3">Firma bulunamadı</p>
          <div>
            <Button
              appearance="primary"
              onClick={() => navigate("/companies")}
              icon={<FiArrowLeft />}
            >
              Cariler
            </Button>
          </div>
        </div>
      </AppPage>
    );
  }

  return (
    <AppPage breadcrumbs={breadcrumbs}>
      <Container>
        {/* ══ Company Header ══ */}
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="card mb-4">
            <div className="card-body p-4">
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-3 bg-primary-subtle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 52, height: 52 }}
                  >
                    <FiHome className="text-primary" size={24} />
                  </div>
                  <div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <h4 className="mb-0 fw-semibold">{company.name}</h4>
                      <Lozenge appearance={getStatusAppearance(company.status)}>
                        {translate(`UI.${company.status?.toUpperCase()}`) ||
                          company.status}
                      </Lozenge>
                    </div>
                    <code className="text-muted small mt-1 d-block">
                      {company.code}
                    </code>
                  </div>
                </div>
                <Button
                  appearance="primary-subtle"
                  onClick={() => navigate("/companies")}
                  icon={<FiArrowLeft />}
                >
                  {translate("UI.BACK") || "Geri"}
                </Button>
              </div>

              <div
                className="d-flex flex-wrap gap-2 pt-3"
                style={{
                  borderTop: "1px solid var(--bs-border-color, #dee2e6)",
                }}
              >
                {[
                  company.email && {
                    icon: <FiMail size={13} />,
                    text: company.email,
                  },
                  company.phone && {
                    icon: <FiPhone size={13} />,
                    text: company.phone,
                  },
                  company.address && {
                    icon: <FiMapPin size={13} />,
                    text: company.address,
                  },
                  company.taxNumber && {
                    icon: <FiHash size={13} />,
                    text: `Vergi No: ${company.taxNumber}${company.taxOffice ? ` / ${company.taxOffice}` : ""}`,
                  },
                  company.yetkiliKisi && {
                    icon: <FiUsers size={13} />,
                    text: `Yetkili Kişi: ${company.yetkiliKisi}`,
                  },
                  company.createdAt && {
                    icon: <FiCalendar size={13} />,
                    text: `${translate("UI.CREATED") || "Oluşturuldu"}: ${formatDate(company.createdAt)}`,
                  },
                ]
                  .filter(Boolean)
                  .map((item, i) => (
                    <span
                      key={i}
                      className="d-inline-flex align-items-center gap-1 text-muted small px-2 py-1 rounded"
                      style={{
                        background: "var(--bs-light, #f8f9fa)",
                        border: "1px solid var(--bs-border-color, #dee2e6)",
                      }}
                    >
                      {item.icon} {item.text}
                    </span>
                  ))}
              </div>
              <div className=" d-flex mt-3 gap-2 row g-3 mb-4">
                {[
                  {
                    icon: <FiShoppingCart size={20} />,
                    value: sales.length,
                    label: "Toplam Sipariş",
                    color: "primary",
                  },
                  {
                    icon: <FiTruck size={20} />,
                    value: deliveries.length,
                    label: "Toplam Sevkiyat",
                    color: "success",
                  },
                  {
                    icon: <FiPackage size={20} />,
                    value: totalDevicesDelivered,
                    label: "Teslim Edilen ürün",
                    color: "warning",
                  },
                ].map((stat, i) => (
                  <div key={i} className="col-4">
                    <div className="card card-body p-3 h-100">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className={`rounded-3 bg-${stat.color}-subtle d-flex align-items-center justify-content-center flex-shrink-0`}
                          style={{ width: 44, height: 44 }}
                        >
                          <span className={`text-${stat.color}`}>
                            {stat.icon}
                          </span>
                        </div>
                        <div>
                          <div className="h4 mb-0 fw-bold">{stat.value}</div>
                          <small className="text-muted">{stat.label}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ContainerItem>

        {/* ══ Stats ══ */}
        <ContainerItem sm={4} md={8} xl={12}></ContainerItem>

        {/* ══ Tabs ══ */}
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="card">
            <Tabs>
              <TabList>
                <Tab>
                  <span className="d-flex align-items-center gap-2">
                    <FiShoppingCart size={14} />
                    Siparişler
                    <span
                      className="badge rounded-pill bg-primary-subtle text-primary"
                      style={{ fontSize: "0.7rem" }}
                    >
                      {sales.length}
                    </span>
                  </span>
                </Tab>
                <Tab>
                  <span className="d-flex align-items-center gap-2">
                    <FiTruck size={14} />
                    Sevkiyatlar
                    <span
                      className="badge rounded-pill bg-success-subtle text-success"
                      style={{ fontSize: "0.7rem" }}
                    >
                      {deliveries.length}
                    </span>
                  </span>
                </Tab>
              </TabList>

              <TabPanel>
                {/* ── SalesMES ── */}
                <Panel>
                  {!hasSalesPage || salesDenied ? (
                    <div className="text-center py-5">
                      <FiLock size={36} className="text-muted mb-3" />
                      <p className="text-muted mb-0 small">
                        Siparişleri görüntülemek için Siparişler sayfası yetkisi
                        gerekir.
                      </p>
                    </div>
                  ) : sales.length === 0 ? (
                    <div className="text-center py-5">
                      <FiShoppingCart size={36} className="text-muted mb-3" />
                      <p className="text-muted mb-0 small">
                        Bu cariye ait sipariş bulunamadı
                      </p>
                    </div>
                  ) : (
                    <div className="p-3">
                      {sales.map((sale, si) => {
                        const saleDeliveries = getSaleDeliveries(sale._id);
                        const deliveredCount = saleDeliveries.reduce(
                          (s, d) => s + (d.devices?.length || 0),
                          0,
                        );
                        const orderedCount = sale.devices?.length || 0;
                        const shouldDeliverTotal =
                          (sale.models || []).reduce(
                            (sum, m) =>
                              sum +
                              (m.count ??
                                m.quantity ??
                                m.qty ??
                                m.amount ??
                                m.total ??
                                0),
                            0,
                          ) || orderedCount;
                        const isExpanded = !!expandedSales[sale._id];

                        return (
                          <div
                            key={sale._id}
                            className="rounded-3 mb-3"
                            style={{
                              border:
                                "1px solid var(--bs-border-color, #dee2e6)",
                              overflow: "hidden",
                            }}
                          >
                            {/* Sale header row */}
                            <div
                              className="d-flex align-items-center justify-content-between gap-2 p-3"
                              style={{
                                cursor: "pointer",
                                background: isExpanded
                                  ? "var(--bs-light, #f8f9fa)"
                                  : "transparent",
                                transition: "background 0.15s",
                              }}
                              onClick={() => toggleSaleExpand(sale._id)}
                            >
                              <div className="d-flex align-items-center gap-3 min-w-0 flex-grow-1">
                                <div
                                  className="rounded-2 bg-primary-subtle d-flex align-items-center justify-content-center flex-shrink-0"
                                  style={{ width: 34, height: 34 }}
                                >
                                  <span className="text-primary fw-bold small">
                                    {si + 1}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <div className="fw-semibold small text-truncate">
                                    {sale?.saleName}
                                  </div>
                                  <div className="d-flex flex-wrap gap-2 mt-1 align-items-center">
                                    <span
                                      className="text-muted d-flex align-items-center gap-1"
                                      style={{ fontSize: "0.72rem" }}
                                    >
                                      <FiCalendar size={10} />
                                      {formatDate(sale.createdAt)}
                                    </span>
                                    {sale.createdBy?.name && (
                                      <span
                                        className="text-muted d-flex align-items-center gap-1"
                                        style={{ fontSize: "0.72rem" }}
                                      >
                                        <FiUser size={10} />
                                        {sale.createdBy.name}
                                      </span>
                                    )}
                                    {sale.totalAmount != null &&
                                      sale.totalAmount > 0 && (
                                        <span
                                          className="fw-semibold d-flex align-items-center gap-1"
                                          style={{
                                            fontSize: "0.72rem",
                                            color: "var(--bs-primary)",
                                          }}
                                        >
                                          {format(sale.totalAmount, "money")}
                                        </span>
                                      )}
                                    {sale.paymentStatus && (
                                      <Lozenge
                                        appearance={
                                          sale.paymentStatus === "paid" ||
                                          sale.paymentStatus === "completed"
                                            ? "success-subtle"
                                            : sale.paymentStatus ===
                                                  "pending" ||
                                                sale.paymentStatus === "partial"
                                              ? "warning-subtle"
                                              : "neutral-subtle"
                                        }
                                        style={{ fontSize: "0.65rem" }}
                                      >
                                        {translate(
                                          `UI.${sale.paymentStatus?.toUpperCase()}`,
                                        ) || sale.paymentStatus}
                                      </Lozenge>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="d-flex align-items-center gap-3 flex-shrink-0">
                                <div
                                  className="text-center d-flex flex-column align-items-center d-none d-sm-block c-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openSaleModelsModal(sale, saleDeliveries);
                                  }}
                                  title="Model bazında teslim / toplam detayını görüntüle"
                                >
                                  <div className="fw-semibold small text-success">
                                    {deliveredCount} / {shouldDeliverTotal}
                                  </div>
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "0.65rem" }}
                                  >
                                    Teslim Edilen / Toplam
                                  </div>
                                </div>
                                <div className="text-center d-none d-sm-block">
                                  <div className="fw-semibold small">
                                    {saleDeliveries.length}
                                  </div>
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "0.65rem" }}
                                  >
                                    Sevkiyat Sayısı
                                  </div>
                                </div>
                                <Lozenge
                                  appearance={getStatusAppearance(sale.status)}
                                >
                                  {translate(
                                    `UI.${sale.status?.toUpperCase()}`,
                                  ) ||
                                    sale.status ||
                                    "-"}
                                </Lozenge>
                                {isExpanded ? (
                                  <FiChevronDown
                                    size={15}
                                    className="text-muted"
                                  />
                                ) : (
                                  <FiChevronRight
                                    size={15}
                                    className="text-muted"
                                  />
                                )}
                              </div>
                            </div>

                            {/* Expanded deliveries */}
                            {isExpanded && (
                              <div
                                style={{
                                  borderTop:
                                    "1px solid var(--bs-border-color, #dee2e6)",
                                }}
                              >
                                {!hasDeliveriesPage || deliveriesDenied ? (
                                  <div className="text-center py-3 text-muted small">
                                    <FiLock size={20} className="mb-2" />
                                    <div>
                                      Sevkiyatları görüntülemek için Sevkiyatlar
                                      sayfası yetkisi gerekir.
                                    </div>
                                  </div>
                                ) : saleDeliveries.length === 0 ? (
                                  <div className="text-center py-3 text-muted small">
                                    Bu sipariş için kayıtlı sevkiyat yok
                                  </div>
                                ) : (
                                  <div className="p-3">
                                    <Table
                                      columns={[
                                        {
                                          name: "Sevkiyat Adı",
                                          dataType: "text",
                                          id: 0,
                                        },
                                        {
                                          name: "Teslim Edilen",
                                          dataType: "number",
                                          id: 1,
                                        },
                                        {
                                          name: "Sevkiyat Tarihi",
                                          dataType: "date",
                                          id: 2,
                                        },
                                        {
                                          name: "Teslim Alan",
                                          dataType: "text",
                                          id: 3,
                                        },
                                        {
                                          name: "Araç Plakası",
                                          dataType: "text",
                                          id: 4,
                                        },
                                        { name: "İşlemler", id: 5 },
                                      ]}
                                      visibleColumns={[0, 1, 2, 3, 4, 5]}
                                      search={false}
                                      hideFooter
                                    >
                                      {saleDeliveries.map((delivery) => {
                                        const totalDevices =
                                          delivery.devices?.length || 0;
                                        return (
                                          <Tr
                                            key={delivery._id}
                                            id={delivery._id}
                                          >
                                            <Td>
                                              <strong>
                                                {limitToThreeWords(
                                                  delivery.deliveryName,
                                                )}
                                              </strong>
                                            </Td>
                                            <Td>
                                              <div className="d-flex align-items-center gap-2">
                                                <FiPackage className="text-info" />
                                                <Lozenge
                                                  appearance="info-subtle"
                                                  style={{ cursor: "pointer" }}
                                                  onClick={() =>
                                                    handleViewDeliveryModels(
                                                      delivery,
                                                    )
                                                  }
                                                  title="Modelleri görüntüle"
                                                >
                                                  {totalDevices}
                                                </Lozenge>
                                              </div>
                                            </Td>
                                            <Td>
                                              {delivery.deliveryDate
                                                ? `${formatDate(delivery.deliveryDate)} ${delivery.deliveryTime || ""}`.trim()
                                                : formatDateTime(
                                                    delivery.transferDate ||
                                                      delivery.createdAt,
                                                  )}
                                            </Td>
                                            <Td>
                                              {delivery.driverName || "-"}
                                            </Td>
                                            <Td>
                                              {delivery.vehiclePlate || "-"}
                                            </Td>
                                            <Td>
                                              <div className="d-flex align-items-center gap-1">
                                                <IconButton
                                                  icon={<FiList size={18} />}
                                                  appearance="subtle"
                                                  shape="circle"
                                                  onClick={() =>
                                                    openSerialModal(delivery)
                                                  }
                                                  title="Seri numaraları"
                                                />
                                                <IconButton
                                                  icon={<FiEye size={18} />}
                                                  appearance="subtle"
                                                  shape="circle"
                                                  onClick={() =>
                                                    navigate(
                                                      `/deliveries/view/${delivery._id}`,
                                                    )
                                                  }
                                                  title="Görüntüle"
                                                />
                                              </div>
                                            </Td>
                                          </Tr>
                                        );
                                      })}
                                    </Table>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Panel>

                {/* ── DeliveriesMES ── */}
                <Panel>
                  {!hasDeliveriesPage || deliveriesDenied ? (
                    <div className="text-center py-5">
                      <FiLock size={36} className="text-muted mb-3" />
                      <p className="text-muted mb-0 small">
                        Sevkiyatları görüntülemek için Sevkiyatlar sayfası
                        yetkisi gerekir.
                      </p>
                    </div>
                  ) : deliveries.length === 0 ? (
                    <div className="text-center py-5">
                      <FiTruck size={36} className="text-muted mb-3" />
                      <p className="text-muted mb-0 small">
                        Bu cariye ait sevkiyat bulunamadı
                      </p>
                    </div>
                  ) : (
                    <div className="p-3">
                      <Table
                        columns={[
                          { name: "Sevkiyat Adı", dataType: "text", id: 0 },
                          { name: "Sipariş", dataType: "text", id: 1 },
                          { name: "Teslim Edilen", dataType: "number", id: 2 },
                          { name: "Sevkiyat Tarihi", dataType: "date", id: 3 },
                          { name: "Teslim Alan Adı", dataType: "text", id: 4 },
                          { name: "Araç Plakası", dataType: "text", id: 5 },
                          { name: "İşlemler", id: 6 },
                        ]}
                        visibleColumns={[0, 1, 2, 3, 4, 5, 6]}
                        search={false}
                        hideFooter
                      >
                        {deliveries.map((delivery) => {
                          const totalDevices = delivery.devices?.length || 0;
                          return (
                            <Tr key={delivery._id} id={delivery._id}>
                              <Td>
                                <strong>
                                  {limitToThreeWords(delivery.deliveryName)}
                                </strong>
                              </Td>
                              <Td>
                                <strong>
                                  {limitToThreeWords(delivery.saleId?.saleName)}
                                </strong>
                              </Td>
                              <Td>
                                <div className="d-flex align-items-center gap-2">
                                  <FiPackage className="text-info" />
                                  <Lozenge
                                    appearance="info-subtle"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      handleViewDeliveryModels(delivery)
                                    }
                                    title="Modelleri görüntüle"
                                  >
                                    {totalDevices}
                                  </Lozenge>
                                </div>
                              </Td>
                              <Td>
                                {delivery.deliveryDate
                                  ? `${formatDate(delivery.deliveryDate)} ${delivery.deliveryTime || ""}`.trim()
                                  : formatDateTime(
                                      delivery.transferDate ||
                                        delivery.createdAt,
                                    )}
                              </Td>
                              <Td>{delivery.driverName || "-"}</Td>
                              <Td>{delivery.vehiclePlate || "-"}</Td>
                              <Td>
                                <div className="d-flex align-items-center gap-1">
                                  <IconButton
                                    icon={<FiList size={18} />}
                                    appearance="subtle"
                                    shape="circle"
                                    onClick={() => openSerialModal(delivery)}
                                    title="Seri numaraları"
                                  />
                                  <IconButton
                                    icon={<FiEye size={18} />}
                                    appearance="subtle"
                                    shape="circle"
                                    onClick={() =>
                                      navigate(
                                        `/deliveries/view/${delivery._id}`,
                                      )
                                    }
                                    title="Görüntüle"
                                  />
                                </div>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Table>
                    </div>
                  )}
                </Panel>
              </TabPanel>
            </Tabs>
          </div>
        </ContainerItem>
      </Container>

      {/* ══ Modals ══ */}
      <SerialModal />
      <SaleModelsModal />
      <Modal
        isOpen={deliveryModelsModalOpen}
        onClose={() => setDeliveryModelsModalOpen(false)}
      >
        <ModalHeader>
          {translate("UI.DELIVERY_MODELS") || "Teslim Edilen Modeller"}
          {selectedDeliveryForModels?.saleId?.saleName && (
            <span className="text-muted small ms-2">
              - {limitToThreeWords(selectedDeliveryForModels.saleId.saleName)}
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          {selectedDeliveryForModels?.devices &&
          selectedDeliveryForModels.devices.length > 0 ? (
            (() => {
              const modelGroups = {};
              (selectedDeliveryForModels.devices || []).forEach((device) => {
                const modelName =
                  device?.modelId?.name ||
                  device?.model?.name ||
                  translate("UI.UNKNOWN_MODEL");
                if (!modelGroups[modelName]) {
                  modelGroups[modelName] = { modelName, count: 0 };
                }
                modelGroups[modelName].count += 1;
              });
              const totalDevices = selectedDeliveryForModels.devices.length;
              const totalModels = Object.keys(modelGroups).length;
              return (
                <>
                  <div className="models-summary-simple mb-3 d-flex gap-3">
                    <div>
                      <span className="text-muted small">
                        {translate("UI.TOTAL_MODELS") || "Model"}:{" "}
                      </span>
                      <strong>{totalModels}</strong>
                    </div>
                    <div>
                      <span className="text-muted small">
                        {translate("UI.TOTAL_DEVICES") || "ürün"}:{" "}
                      </span>
                      <strong className="text-success">{totalDevices}</strong>
                    </div>
                  </div>
                  {Object.values(modelGroups).map((mg, i) => (
                    <div
                      key={i}
                      className="model-item-simple d-flex align-items-center justify-content-between mb-2 p-2 rounded"
                      style={{
                        border: "1px solid var(--bs-border-color, #dee2e6)",
                      }}
                    >
                      <span>{mg.modelName}</span>
                      <Lozenge appearance="success-subtle">{mg.count}</Lozenge>
                    </div>
                  ))}
                </>
              );
            })()
          ) : (
            <div className="text-center text-muted small py-4">
              {translate("UI.NO_DEVICES_FOUND") || "ürün bulunamadı"}
            </div>
          )}
        </ModalBody>
      </Modal>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            {...t}
            onClose={() => removeToast(t.id)}
            onDismissed={() => removeToast(t.id)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(CompanyDetails, ["admin", "employee", "user"]);
